import {
    type Falsy,
    type FocusEvent,
    type Role,
    View,
    type ViewStyle,
    type AccessibilityProps,
    type LayoutChangeEvent,
} from 'react-native';

export type BoxRef = View;
export type BoxStyle = ViewStyle | Falsy;
export type BoxStyles = BoxStyle | BoxStyle[];

export interface BoxProps extends AccessibilityProps {
    readonly collapsable?: boolean;
    readonly ref?: React.Ref<View>;
    readonly onLayout?: (event: LayoutChangeEvent) => void;
    readonly onFocus?: ((event: FocusEvent) => void) | null;
    readonly accessible?: boolean;
    readonly children?: React.ReactNode;
    readonly id?: string | undefined;
    /** Controls whether the View can be the target of touch events */
    readonly pointerEvents?: 'auto' | 'none' | 'box-none' | 'box-only';
    readonly role?: Role;
    readonly sx?: BoxStyles;
    /** Unique identifier for accessibility labelling (Android) */
    readonly nativeID?: string;
    /** Controls accessibility importance for screen readers */
    readonly importantForAccessibility?: 'auto' | 'yes' | 'no' | 'no-hide-descendants';
    readonly focusable?: boolean;
    readonly testID?: string;
}

// Caveat: RN View only supports `flex` and `none` displays. So all View wrapper
// components are equivalent. Creating these components so it's slightly easier to
// import during migration.
export function Box({
    ref,
    onLayout,
    onFocus,
    children,
    sx,
    id,
    accessible,
    pointerEvents,
    role,
    nativeID,
    importantForAccessibility,
    focusable,
    collapsable,
    testID,
    ...props
}: BoxProps): React.JSX.Element {
    return (
        <View
            onLayout={onLayout}
            onFocus={onFocus}
            ref={ref}
            accessible={accessible}
            id={id}
            pointerEvents={pointerEvents}
            role={role}
            nativeID={nativeID}
            importantForAccessibility={importantForAccessibility}
            style={sx}
            /**
             * testID was originally set by id. To preserve backwards compatibility,
             * we need to set it to the new testID if it is provided. If not, we use
             * the original id.
             */
            testID={testID ?? id}
            focusable={focusable}
            collapsable={collapsable}
            {...props}
        >
            {children}
        </View>
    );
}
